import { useEffect, useState } from "react";
import { Dialog, Text } from "@primer/react";
import { Alert } from "../components/Alert/Alert.jsx";
import { Button } from "../components/Button/Button.jsx";
import { FormBody, FormContainer, FormFooter, FormHeader } from "../components/FormContainer/FormContainer.jsx";
import { OpenSharedLinkDialog } from "../components/SharedProfile/OpenSharedLinkDialog.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { SharedProfileStatus, SharedProfileSummary } from "../components/SharedProfile/SharedProfile.jsx";
import { uniqueImportedProfileName } from "../domain/profileShare.js";
import { loadSharedProfile } from "../lib/sharedProfileApi.js";
import { sharedRouteParts } from "../lib/sharedProfileLink.js";
import {
  rememberSharedProfile, rememberSharedProfileLocalRevision, rememberSharedProfileSavedAt
} from "../storage/profileSharingPreferences.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import { ProfilesPage } from "./ProfilesPage.jsx";
import "./SharedProfilePage.css";

export function SharedProfilePage({ fetchRequest = fetch, onSyncConnectionChange, ready = true, routeValue = "" }) {
  const route = sharedRouteParts(routeValue);
  if (!ready) return route.token && route.mode === "sync"
    ? <ProfilesPage />
    : <article className="profile-share-page" aria-busy="true"><PageHeading>Shared Link</PageHeading></article>;
  if (!route.token) return <><ProfilesPage /><OpenSharedLinkDialog onClose={() => { window.location.hash = "#/profiles"; }} /></>;
  return <IncomingSharedProfilePage fetchRequest={fetchRequest} onSyncConnectionChange={onSyncConnectionChange} route={route} />;
}

function IncomingSharedProfilePage({ fetchRequest, onSyncConnectionChange, route }) {
  const application = useApplicationDocument();
  const [loaded, setLoaded] = useState(null);
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(true);

  useEffect(() => {
    let active = true;
    loadSharedProfile(fetchRequest, route.token)
      .then((result) => { if (active) setLoaded(result); })
      .catch((error) => { if (active) setStatus({ tone: "danger", text: error.message }); })
      .finally(() => { if (active) setWorking(false); });
    return () => { active = false; };
  }, [fetchRequest, route.token]);

  async function importCopy() {
    if (!loaded) return;
    setWorking(true);
    const saved = await application.importProfile({ name: uniqueImportedProfileName(application.profiles, loaded.share.profile.name), data: loaded.share.profile.data });
    if (saved) window.location.hash = "#/compliance";
    else {
      setStatus({ tone: "danger", text: "The shared profile could not be imported." });
      setWorking(false);
    }
  }

  async function connectSync() {
    if (!loaded || !application.profile) return;
    setWorking(true);
    const profileId = application.profile.id;
    const localRevision = new Date().toISOString();
    const saved = await application.replaceProfileData(profileId, loaded.share.profile.data, localRevision);
    if (saved) {
      rememberSharedProfile(profileId, route.token);
      rememberSharedProfileSavedAt(profileId, loaded.updatedAt);
      rememberSharedProfileLocalRevision(profileId, localRevision);
      onSyncConnectionChange?.();
      window.location.hash = "#/profiles";
    } else {
      setStatus({ tone: "danger", text: "The profile could not be connected." });
      setWorking(false);
    }
  }

  const sharingCopy = route.mode === "share";
  if (!sharingCopy) return <><ProfilesPage /><ConnectServerSyncDialog application={application} loaded={loaded} onClose={() => { if (!working || !loaded) window.location.hash = "#/profiles"; }} onConnect={connectSync} status={status} working={working} /></>;

  return <article className="profile-share-page">
    <PageHeading description="Import a detached copy that will not remain connected.">Import Shared Profile</PageHeading>
    <FormContainer>
      <FormHeader heading={loaded ? loaded.share.profile.name : "Shared Profile"} description="Changes to the imported profile will remain on this device." />
      <FormBody>{working ? <Text className="muted-text">Loading shared profile…</Text> : loaded ? <SharedProfileSummary share={loaded.share} /> : <Text className="muted-text">The shared profile could not be loaded.</Text>}</FormBody>
      <FormFooter><div>{loaded ? <Button disabled={working || application.saving} onClick={importCopy}>Import as New Profile</Button> : null}</div><SharedProfileStatus status={status} /></FormFooter>
    </FormContainer>
  </article>;
}

function ConnectServerSyncDialog({ application, loaded, onClose, onConnect, status, working }) {
  const profileName = application.profile?.name ?? "the active profile";
  const connecting = working && Boolean(loaded);
  return <Dialog
    className="connect-server-sync-dialog"
    title="Connect Server Sync"
    subtitle="Replace local data and connect to a Server Sync."
    onClose={onClose}
  >
    <div className="connect-server-sync-body">
      {working && !loaded ? <Text className="muted-text">Loading shared profile…</Text> : loaded ? <><SharedProfileSummary share={loaded.share} /><Alert tone="warning" heading={`Replace local data for ${profileName} profile?`}>Future saves and loads will use this sync point.</Alert></> : <Text className="muted-text">The shared profile could not be loaded.</Text>}
    </div>
    <Dialog.Footer className="connect-server-sync-footer">
      <div className="connect-server-sync-status"><SharedProfileStatus status={status} /></div>
      <div className="connect-server-sync-actions">
        <Button tone="secondary" onClick={onClose} disabled={connecting}>Cancel</Button>
        {loaded ? <Button tone="danger" onClick={onConnect} disabled={connecting || application.saving}>{connecting ? "Connecting…" : `Connect ${profileName}`}</Button> : null}
      </div>
    </Dialog.Footer>
  </Dialog>;
}
